/**
 * The intake form schema (BACKLOG §15).
 *
 * Mirrors services/orchestrator/model/formfield.go. That file validates every
 * write; this one is what the builder edits and the viewer renders. If the two
 * drift, the server wins and the builder starts producing forms the API
 * rejects — so change them together.
 *
 * Ported from the-answer-2's call-type form model, with one deliberate fix:
 * there, a field's identity was `callTypeFormFieldId`, a client-assigned
 * NUMBER, sitting beside an unrelated `id` string. Here identity is a single
 * stable string `id`, and `key` carries the semantic name separately.
 */

export const FIELD_TYPES = [
  "text",
  "textarea",
  "phone",
  "email",
  "extension",
  "date",
  "time",
  "date-time",
  "radio-group",
  "checkbox",
  "note",
  "link",
  "address-line-1",
  "address-line-2",
  "locality",
  "county",
  "region",
  "country",
  "postal-code",
] as const;

export type FieldType = (typeof FIELD_TYPES)[number];

/** Human labels for the type picker, in the order the palette shows them. */
export const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  text: "Single line",
  textarea: "Multi-line",
  phone: "Phone",
  email: "Email",
  extension: "Extension",
  date: "Date",
  time: "Time",
  "date-time": "Date and time",
  "radio-group": "Choose one",
  checkbox: "Yes / no",
  note: "Note to agent",
  link: "Link",
  "address-line-1": "Address line 1",
  "address-line-2": "Address line 2",
  locality: "City",
  county: "County",
  region: "State",
  country: "Country",
  "postal-code": "ZIP / postcode",
};

/** Types whose value comes from a fixed list. Only these carry `options`. */
export function hasOptions(type: FieldType): boolean {
  return type === "radio-group";
}

/** A note renders guidance and captures nothing, so it has no value. */
export function capturesValue(type: FieldType): boolean {
  return type !== "note" && type !== "link";
}

export interface FormField {
  /**
   * Identity. Generated once, never reused, never derived from the label —
   * submissions record values against it, so renaming a label must not orphan
   * an existing message.
   */
  id: string;
  /**
   * The semantic name: "first_name", "callback_number". Delivery templates
   * and integrations read by key, so an email template keeps working when a
   * form is rebuilt. Lowercase letters, digits and underscores.
   */
  key: string;
  type: FieldType;
  label: string;
  placeholder?: string;
  description?: string;
  required?: boolean;
  /** Choices for a radio group. Absent on every other type. */
  options?: string[];
  /** Standing guidance beside the field; a "note" field is only its note. */
  note?: string;
  /** Backs a "link" field: something the agent opens during the call. */
  url?: string;
  /** Section heading. Fields sharing a group render together. */
  group?: string;
  /**
   * A template may ship fields the operator can reorder but must not remove
   * or rename — the ones delivery depends on.
   */
  deletable: boolean;
  editable: boolean;
}

/**
 * What a field holds. A string, or null when the agent marks it uncollected —
 * "Caller Refused To Provide". Upstream typed this `any`; every renderer
 * actually reads and writes strings.
 */
export type FieldValue = string | null;

/** The cap the server enforces; the builder refuses to add past it. */
export const MAX_FORM_FIELDS = 200;

const KEY_PATTERN = /^[a-z][a-z0-9_]*$/;

/**
 * Derives a key from a label: "Callback Number" → "callback_number". The
 * builder offers this and the operator may override it, because the key is
 * a contract with delivery templates and the label is not.
 */
export function deriveFieldKey(label: string): string {
  const slug = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (!slug) return "field";
  return /^[a-z]/.test(slug) ? slug : `field_${slug}`;
}

/**
 * Makes `key` unique within a form by suffixing. Used when a field is added
 * or duplicated, so the builder never produces a document the server rejects.
 */
export function uniqueFieldKey(base: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  if (!used.has(base)) return base;
  for (let n = 2; ; n++) {
    const candidate = `${base}_${n}`;
    if (!used.has(candidate)) return candidate;
  }
}

/** A fresh field identity. crypto.randomUUID is available in every target browser. */
export function newFieldId(): string {
  return crypto.randomUUID();
}

/**
 * The same rules as the Go validator, returning the first problem so the
 * builder can block a save before the round trip. The server still validates;
 * this is for the message, not the guarantee.
 */
export function validateFormFields(fields: FormField[]): string | null {
  if (fields.length > MAX_FORM_FIELDS) {
    return `This form has ${fields.length} fields; the limit is ${MAX_FORM_FIELDS}.`;
  }
  const ids = new Set<string>();
  const keys = new Set<string>();

  let i = 0;
  for (const f of fields) {
    const at = `Field ${++i}`;

    if (!f.id?.trim()) return `${at} has no id.`;
    if (ids.has(f.id)) return `${at} repeats an id.`;
    ids.add(f.id);

    if (!KEY_PATTERN.test(f.key ?? "")) {
      return `${at} has the key "${f.key}". Keys are lowercase letters, digits and underscores, starting with a letter.`;
    }
    if (keys.has(f.key)) {
      return `${at} repeats the key "${f.key}". Keys name the value in delivery templates, so they must be unique.`;
    }
    keys.add(f.key);

    if (!FIELD_TYPES.includes(f.type)) return `${at} has an unknown type.`;

    if (f.type === "note") {
      if (!f.note?.trim()) return `${at} is a note with no text.`;
    } else if (!f.label?.trim()) {
      return `${at} has no label.`;
    }

    if (hasOptions(f.type)) {
      const options = f.options ?? [];
      if (options.length < 2) return `${at} needs at least 2 options.`;
      if (options.some((o) => !o.trim())) return `${at} has a blank option.`;
      if (new Set(options).size !== options.length) return `${at} repeats an option.`;
    } else if ((f.options?.length ?? 0) > 0) {
      return `${at} is a ${FIELD_TYPE_LABELS[f.type]} field and cannot carry options.`;
    }

    if (f.type === "link" && !f.url?.trim()) return `${at} is a link with no url.`;
  }
  return null;
}

/**
 * Groups fields for rendering, preserving document order and the order each
 * group first appears. Ungrouped fields carry a null group and render on
 * their own, in place.
 */
export function groupFields(
  fields: FormField[],
): { group: string | null; fields: FormField[] }[] {
  const out: { group: string | null; fields: FormField[] }[] = [];
  for (const f of fields) {
    const group = f.group?.trim() ? f.group : null;
    const last = out[out.length - 1];
    if (group !== null && last?.group === group) {
      last.fields.push(f);
    } else {
      out.push({ group, fields: [f] });
    }
  }
  return out;
}
